import {
  ChevronDown,
  ExternalLink,
  Heart,
  Instagram,
  MessageCircle,
  Music2,
  Play,
  User,
} from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { instagramFetchPosts } from 'sprites-types/workflow/tools/instagramFetchPosts'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

type NormalizedMusicInfo = {
  songName: string | null
  artistName: string | null
  usesOriginalAudio: boolean | null
  audioId: string | null
}

export const normalizeInstagramMusicInfo = (post: any): NormalizedMusicInfo | null => {
  const rawMusicInfo = post?.musicInfo || post?.music_info

  if (!rawMusicInfo) {
    return null
  }

  const songName = rawMusicInfo.songName ?? rawMusicInfo.song_name ?? null
  const artistName = rawMusicInfo.artistName ?? rawMusicInfo.artist_name ?? null
  const audioId = rawMusicInfo.audioId ?? rawMusicInfo.audio_id ?? null
  const usesOriginalAudio =
    rawMusicInfo.usesOriginalAudio ?? rawMusicInfo.uses_original_audio ?? null

  if (!songName && !artistName && !audioId && usesOriginalAudio === null) {
    return null
  }

  return {
    songName,
    artistName,
    audioId,
    usesOriginalAudio,
  }
}

export function InstagramPostItem({ post, className }: { post: any; className?: string }) {
  const caption = post.caption || post.text || post.description || ''
  const primaryMedia = Array.isArray(post.media) ? post.media[0] : undefined
  const derivedImageUrl =
    primaryMedia?.imageUrl ||
    primaryMedia?.display_url ||
    post.media_url ||
    post.image_url ||
    post.thumbnail_url ||
    null
  const derivedVideoUrl =
    primaryMedia?.videoUrl || primaryMedia?.video_url || post.video_url || null
  const mediaType = (
    primaryMedia?.type ||
    primaryMedia?.media_type ||
    post.media_type ||
    post.type ||
    ''
  )
    .toString()
    .toUpperCase()
  const isVideo =
    mediaType.includes('VIDEO') ||
    mediaType.includes('REEL') ||
    mediaType.includes('CLIP') ||
    !!derivedVideoUrl
  const mediaUrl = isVideo ? derivedVideoUrl || derivedImageUrl : derivedImageUrl
  const permalink = post.permalink || post.link || post.url
  const timestamp = post.timestamp || post.created_time || post.date
  const likeCount = post.like_count || post.likes || 0
  const commentsCount = post.comments_count || post.comments || 0
  const username = post.username || post.user || post.author
  const musicInfo = normalizeInstagramMusicInfo(post)

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return dateStr
    }
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-100">
            <User className="h-4 w-4 text-pink-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">
              {username ? `@${username}` : 'Instagram Post'}
            </div>
            {timestamp && <div className="text-xs text-gray-600">{formatDate(timestamp)}</div>}
          </div>
        </div>

        {mediaUrl && (
          <div className="mb-3 overflow-hidden rounded-2xl bg-gray-100">
            {isVideo ? (
              <div className="relative">
                <video
                  src={mediaUrl}
                  className="max-h-80 w-full object-cover"
                  controls
                  preload="metadata"
                />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <Play className="h-12 w-12 text-white/80" />
                </div>
              </div>
            ) : (
              <Image
                src={mediaUrl}
                alt="Instagram post"
                className="max-h-80 w-full object-cover"
                width={400}
                height={320}
                unoptimized
              />
            )}
          </div>
        )}

        {caption && (
          <div className="mb-3 whitespace-pre-wrap text-xs text-gray-700">
            {caption.length > 300 ? `${caption.slice(0, 300)}...` : caption}
          </div>
        )}

        {musicInfo && (
          <div className="mb-3 rounded-2xl border border-pink-100/70 bg-pink-50/80 px-3 py-2">
            <div className="flex items-center justify-between text-xs text-pink-800">
              <div className="flex items-center gap-2 font-semibold">
                <Music2 className="h-4 w-4 text-pink-600" />
                <span>{musicInfo.songName || 'Audio track'}</span>
              </div>
              {musicInfo.usesOriginalAudio && (
                <span className="rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-pink-600">
                  Original audio
                </span>
              )}
            </div>
            {(musicInfo.artistName || musicInfo.audioId) && (
              <div className="mt-1 text-[11px] text-pink-700">
                {musicInfo.artistName && <span>by {musicInfo.artistName}</span>}
                {musicInfo.audioId && (
                  <span className="ml-1 text-pink-500/80">• Audio ID: {musicInfo.audioId}</span>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-gray-600">
            {likeCount > 0 && (
              <div className="flex items-center gap-1">
                <Heart className="h-3 w-3 text-red-500" />
                <span>{formatNumber(likeCount)}</span>
              </div>
            )}
            {commentsCount > 0 && (
              <div className="flex items-center gap-1">
                <MessageCircle className="h-3 w-3 text-gray-500" />
                <span>{formatNumber(commentsCount)}</span>
              </div>
            )}
          </div>
          {permalink && (
            <a
              href={permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-pink-600 hover:text-pink-800"
            >
              <ExternalLink className="h-3 w-3" />
              View Post
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function InstagramFetchPostsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(instagramFetchPosts, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const username = inputs.username || inputs.user || inputs.account || 'user'
  const posts = Array.isArray(result)
    ? result
    : (result as any)?.data || (result as any)?.posts || (result ? [result] : [])
  const totalLikes = posts.reduce((sum: number, p: any) => sum + (p.like_count || p.likes || 0), 0)

  if (posts.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Instagram className="h-5 w-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">No posts found</div>
            <div className="text-xs text-gray-600">Account: @{username}</div>
          </div>
        </div>
      </div>
    )
  }

  if (posts.length === 1) {
    return <InstagramPostItem post={posts[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Instagram className="h-5 w-5 text-pink-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Instagram Posts</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {posts.length} posts • @{username} • {totalLikes.toLocaleString()} likes
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {posts.map((post: any, index: number) => (
            <InstagramPostItem
              key={post.id || post.permalink || index}
              post={post}
              className="mx-8"
            />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadInstagramFetchPosts(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function instagramFetchPostsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(instagramFetchPosts, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const username = inputs.username || inputs.user || inputs.account || 'user'
  const posts = Array.isArray(result)
    ? result
    : (result as any)?.data || (result as any)?.posts || (result ? [result] : [])

  if (posts.length === 0) {
    return `## Instagram Posts

**Account:** @${username}

No posts found.

---
`
  }

  const tableHeader = `| Post | Date | Likes | Comments | Caption | Media | Link |
|------|------|-------|----------|---------|-------|------|`

  const tableRows = posts
    .map((post: any, index: number) => {
      const postNum = `#${index + 1}`
      const timestamp = post.timestamp || post.created_time || post.date
      const date = timestamp ? new Date(timestamp).toLocaleDateString() : '-'
      const likeCount = post.like_count || post.likes || 0
      const likes = likeCount > 0 ? likeCount.toLocaleString() : '-'
      const commentsCount = post.comments_count || post.comments || 0
      const comments = commentsCount > 0 ? commentsCount.toLocaleString() : '-'
      const caption = (post.caption || post.text || post.description || '-')
        .replace(/\|/g, '\\|')
        .replace(/\n/g, ' ')
        .slice(0, 50)
      const captionDisplay = caption.length === 50 ? caption + '...' : caption
      const mediaUrl = post.media_url || post.image_url || post.video_url || post.url
      const mediaDisplay = mediaUrl ? `[Media](${mediaUrl})` : '-'
      const permalink = post.permalink || post.link || post.url
      const linkDisplay = permalink ? `[View](${permalink})` : '-'

      return `| ${postNum} | ${date} | ${likes} | ${comments} | ${captionDisplay} | ${mediaDisplay} | ${linkDisplay} |`
    })
    .join('\n')

  const detailedCaptions = posts
    .filter((post: any) => (post.caption || post.text || post.description)?.length > 50)
    .slice(0, 5) // Limit to first 5 detailed captions
    .map((post: any, index: number) => {
      const caption = post.caption || post.text || post.description || ''
      const timestamp = post.timestamp || post.created_time || post.date
      const date = timestamp ? new Date(timestamp).toLocaleDateString() : ''
      return `### Post ${index + 1}${date ? ` - ${date}` : ''}\n\n${caption}`
    })
    .join('\n\n')

  return `## Instagram Posts (${posts.length} posts)

**Account:** @${username}

${tableHeader}
${tableRows}

${detailedCaptions ? `\n## Detailed Captions\n\n${detailedCaptions}\n` : ''}
---
`
}

export const InstagramFetchPostsDisplay = createWorkflowToolComponent(
  InstagramFetchPostsDisplayComponent,
  downloadInstagramFetchPosts,
  instagramFetchPostsToMarkdown
)
